import * as React from 'react';
import styled, { css } from 'styled-components';

import { ButtonReset } from '../Button/index';
import { size } from '../layout/helpers';
import { fontBold } from '../layout/font';

export const Wrapper = styled.div`
  --focus-outline-offset: -0.125rem;
  --tabs-popout: ${size(3)};

  ${(props) => props.theme.breakpoint.LtSm} {
    --tabs-popout: ${size(2)};
  }

  position: relative;
  overflow: hidden;
  margin: 0 calc(var(--tabs-popout) * -1);

  ${(props) => props.theme.breakpoint.Md} {
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      height: 100%;
      width: var(--tabs-popout);
      z-index: 1;
    }

    &::before {
      left: 0;
      background-image: linear-gradient(
        to right,
        var(--bg-body, ${(props) => props.theme.color.bg}),
        rgba(255, 255, 255, 0)
      );
    }

    &::after {
      right: 0;
      background-image: linear-gradient(
        to left,
        var(--bg-body, ${(props) => props.theme.color.bg}),
        rgba(255, 255, 255, 0)
      );
    }
  }
`;

export const List = styled.ul.attrs(() => ({
  role: 'tablist',
}))`
  display: flex;
  align-items: flex-end;
  list-style: none;
  padding: 0 var(--tabs-popout);
  margin: 0;
  overflow: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-left: var(--tabs-popout);
  scroll-padding-right: var(--tabs-popout);
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Item = styled.li.attrs(() => ({
  role: 'presentation',
}))`
  scroll-snap-align: start;

  & + & {
    margin-left: ${size(1)};
  }
`;

type ActionProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  $isActive?: boolean | string;
};

export const Action = styled(ButtonReset).attrs(() => ({
  role: 'tab',
}))<ActionProps>`
  ${fontBold};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${size(1.5)} ${size(3)};
  background-color: var(--button-secondary-bg-color, ${(props) => props.theme.color.accentDark});
  color: var(--button-secondary-text-color, ${(props) => props.theme.color.primary});
  font-size: 0.875rem;
  line-height: 1.2;
  border-radius: var(--button-border-radius, 3px);
  text-decoration: none;
  white-space: nowrap;
  transform-origin: 50% 50%;
  transition: transform 300ms cubic-bezier(0.47, 1.64, 0.41, 0.8);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 150ms ease;
  }

  &:not([disabled]):hover {
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.97);
  }

  &[disabled] {
    opacity: 0.37;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: var(--button-primary-bg-color, ${(props) => props.theme.color.primary});
      color: var(--button-primary-text-color, ${(props) => props.theme.color.textLight});
    `}
`;
